import { useDropzone } from "react-dropzone";
import type { GameHistoryNoteFileResponse } from "libs/apiClient";
import { _ } from "i18n";
import DeleteIcon from "ui-kit/assets/delete/delete.svg";
import { GAME_HISTORY_ACCEPTED_FILE_TYPES, type GameHistoryPendingFile } from "./constants";

interface GameHistoryNoteEditFormProps {
	editText: string;
	existingFiles: GameHistoryNoteFileResponse[];
	editDeletedFileIds: number[];
	editNewFiles: GameHistoryPendingFile[];
	isSaving: boolean;
	onTextChange: (text: string) => void;
	onRemoveExistingFile: (fileId: number) => void;
	onFileDrop: (files: File[]) => void;
	onRemoveNewFile: (fileId: string) => void;
	onSave: () => void;
	onCancel: () => void;
}

export function GameHistoryNoteEditForm({
	editText,
	existingFiles,
	editDeletedFileIds,
	editNewFiles,
	isSaving,
	onTextChange,
	onRemoveExistingFile,
	onFileDrop,
	onRemoveNewFile,
	onSave,
	onCancel,
}: GameHistoryNoteEditFormProps) {
	const { getRootProps, getInputProps, open } = useDropzone({
		onDrop: onFileDrop,
		accept: GAME_HISTORY_ACCEPTED_FILE_TYPES,
		noClick: true,
		noKeyboard: true,
	});

	const visibleExistingFiles = existingFiles.filter(
		(file) => editDeletedFileIds.includes(file.id) === false,
	);
	const hasAttachedFiles = visibleExistingFiles.length > 0 || editNewFiles.length > 0;

	return (
		<div className="discoverGameHistory-editForm" {...getRootProps()}>
			<input {...getInputProps()} />
			<textarea
				className="discoverGameHistory-editTextarea"
				value={editText}
				onChange={(e) => onTextChange(e.target.value)}
			/>
			{hasAttachedFiles && (
				<div className="discoverGameHistory-attachedFiles discoverGameHistory-attachedFiles--edit">
					{visibleExistingFiles.map((file) => (
						<div key={file.id} className="discoverGameHistory-attachedFile">
							<a
								href={file.url}
								target="_blank"
								rel="noopener noreferrer"
								className="discoverGameHistory-attachedFileLink"
								onClick={(e) => e.stopPropagation()}
							>
								{file.filename}
							</a>
							<button
								type="button"
								onClick={() => onRemoveExistingFile(file.id)}
								className="discoverGameHistory-removeFileBtn"
								aria-label={_("btn.delete")}
							>
								<img src={DeleteIcon} alt="" />
							</button>
						</div>
					))}
					{editNewFiles.map((f) => (
						<div key={f.id} className="discoverGameHistory-attachedFile">
							<span>{f.file.name}</span>
							<button
								type="button"
								onClick={() => onRemoveNewFile(f.id)}
								className="discoverGameHistory-removeFileBtn"
								aria-label={_("btn.delete")}
							>
								<img src={DeleteIcon} alt="" />
							</button>
						</div>
					))}
				</div>
			)}
			<div className="discoverGameHistory-editActions">
				<button
					type="button"
					className="discoverGameHistory-attachBtn discoverGameHistory-attachBtn--edit"
					onClick={open}
				>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M14 8.667l-5.293 5.293a3.333 3.333 0 01-4.714-4.713L9.64 3.6a2.222 2.222 0 013.143 3.143L7.133 12.4a1.111 1.111 0 01-1.572-1.572l5.293-5.293" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</button>
				<button
					type="button"
					className="discoverGameHistory-submitBtn"
					onClick={onSave}
					disabled={isSaving}
					aria-label={_("discover.gameDetail.history.save")}
				>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M14.667 1.333L7.333 8.667M14.667 1.333l-4.667 13.334-3-6.667-6.667-3 13.334-4.667z" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</button>
				<button
					type="button"
					className="discoverGameHistory-attachBtn discoverGameHistory-attachBtn--edit"
					onClick={onCancel}
					aria-label={_("discover.gameDetail.history.cancel")}
				>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</button>
			</div>
		</div>
	);
}
